// They should be the same number
import styled from 'astroturf/react';
import MobileChipizeBase from 'js/search/react/MobileChipize';
import {
  FilterOption,
  HashifiedStats,
  LangCode,
  PubType,
  VideoCategory,
} from 'js/search/react/util';
import PremiumIcon from 'js/premium/PremiumIcon';
import Filter, { Title as FilterTitle } from 'js/search/react/Filter';
import DateFilter from 'js/search/react/DateFilter/DateFilter';
import React, { Ref } from 'react';

const MOBILE_WIDTH = 750;

const Filters = styled.aside`
  background: #fff;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  grid-row: 1 / 5;
  padding: 20px 23px;
  position: sticky;
  top: 0;

  @media (max-width: ${MOBILE_WIDTH}px) {
    align-items: flex-start;
    background: transparent;
    box-shadow: none;
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    position: static;
  }
`;

export const Label = styled.h2<{ marginBottom?: boolean }>`
  align-items: center;
  color: #222;
  display: flex;
  font-size: 14px;
  letter-spacing: 0.08em;
  margin: 0;
  text-transform: uppercase;

  &.marginBottom {
    margin-bottom: 22px;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    margin-right: 8px;

    &.marginBottom {
      margin-bottom: 10px;
    }
  }
`;

const MobileChipize = styled(MobileChipizeBase)`
  margin-bottom: 10px;
  margin-right: 8px;
`;

interface SearchFilterDefinition<T> {
  options: FilterOption<T>[];
  onChange: (value: string) => void;
  selected: T[];
}

type Props = {
  startYear: number;
  endYear: number;
  dateFilterRef: Ref<{ resetYears(): void }>;
  setDateRangeYears: (years: Array<number>) => void;
  stats?: HashifiedStats | null;
  loading: boolean;
  langs: SearchFilterDefinition<LangCode>;
  pubTypes?: SearchFilterDefinition<PubType>;
  videoCategories?: SearchFilterDefinition<VideoCategory>;
};

export const SearchFilters: React.FC<Props> = ({
  startYear,
  endYear,
  dateFilterRef,
  setDateRangeYears,
  stats,
  loading,
  langs,
  pubTypes,
  videoCategories,
}) => {
  return (
    <Filters>
      <Label marginBottom>
        <PremiumIcon size={20} />
        Filters
      </Label>
      <MobileChipize name="Date Range" width={MOBILE_WIDTH}>
        <>
          <FilterTitle>Date Range</FilterTitle>
          <DateFilter
            initialYears={[startYear, endYear]}
            ref={dateFilterRef}
            setDateRangeYears={setDateRangeYears}
            worksPerYear={stats?.publicationYearCounts}
            loading={loading}
            mobileSize={MOBILE_WIDTH}
          />
        </>
      </MobileChipize>
      {pubTypes != null && (
        <MobileChipize name="Publication Type" width={MOBILE_WIDTH}>
          <Filter
            onChange={pubTypes.onChange}
            options={pubTypes.options}
            selected={new Set(pubTypes.selected)}
            title="Publication Type"
          />
        </MobileChipize>
      )}
      {videoCategories != null && (
        <MobileChipize name="Video Type" width={MOBILE_WIDTH}>
          <Filter
            onChange={videoCategories.onChange}
            options={videoCategories.options}
            selected={new Set(videoCategories.selected)}
            title="Video Type"
          />
        </MobileChipize>
      )}
      <MobileChipize name="Language" width={MOBILE_WIDTH}>
        <Filter
          limit={6}
          multi
          onChange={langs.onChange}
          options={langs.options}
          selected={new Set(langs.selected)}
          title="Language"
        />
      </MobileChipize>
    </Filters>
  );
};
